import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Jspreadsheet, JspreadsheetCellUpdate } from "Components/General/Jspreadsheet";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { AccessRightsCell, accessRightSetCellColor } from "./AcessRightsCell";
import { Row, Col, message, Input, Select, Popconfirm, Space } from "antd";
import { AccessRightsExplanation } from "./AccessRightsExplanation";
import { MessageType } from "antd/es/message/interface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce, groupBy, orderBy } from "lodash";
import { CanvasLoader } from "Components/General/Loader";
import { Button } from "Components/General/Button";
// import { GenericSidebar } from "Components/GenericSidebar";
import { download } from "utils/download";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { notificationError } from "utils/libHelpers/AntdNotification";
import jspreadsheet from "jspreadsheet";
import { until } from "utils/until";
import { ModalCloseBtn } from "Components/General/Modal/ModalCloseBtn";
import { RemovedUsersNote } from "./RemovedUsersNote";
import { simplePopover } from "utils/simplePopover";
import { crE } from "utils/simpleDOMHelpers";

interface AccessRightsMatrixProps extends WithTranslation, ModalComponentProps {
  folderId: number;
}

const ICON_RIGHT = "▸";
const ICON_DOWN = "▾";
const ICON_SPIN = "◠";

interface ErrorColumn {
  rowNumber: number;
  colNumber: number;
  value: API_ColumnValue;
}

type SearchMode = "FOLDER" | API_COLUMNDEFINITION_TYPE; // to select which term to search.

interface AccessRightsMatrixState {
  data: API_RightsMatrixResponse;
  updatedAccessRights: API_ColumnValueUpdate[];
  loading: boolean;
  arrayOfFolders: Array<Array<string | number | boolean | API_ColumnValue>>;
  savedFolderId: number; // used to preserve and re-assign folder id (Feature to expand folder via context menu)
  searchMode: SearchMode;
  errorCells: ErrorColumn[];
  errorCellsCurrent: number;
  searchQuery: string;
  closeSideBar: boolean;
  loadingExcel: boolean;
  skipUpdate: boolean;
  expandLoading: boolean;
  headerBackgroundColors: string[];
  hasRemovedUsers: boolean;
}

export const AccessRightsMatrix = withTranslation(I18N_NS.PROJECT)(
  class AccessRightsMatrixClass extends React.Component<AccessRightsMatrixProps, AccessRightsMatrixState> {
    jspreadsheetRef: React.RefObject<Jspreadsheet>;
    search: () => void;
    debounceOnSelection: Jspreadsheet["onSelection"];
    folderIdToRow = {};
    checkUpdatesDebounce: () => Promise<void>;
    basicColumns: jspreadsheet.Column[] = [
      {
        type: "hidden",
        // integer - folderId
      },
      {
        type: "hidden",
        // boolean - has children
      },
      {
        type: "html",
        width: 20,
        title: " ",
        readOnly: true,
        // expand/collapse icon
      },
      {
        title: "Level",
        type: "hidden",
        // depth level of folder
      },
      {
        title: this.props.t("Foldername"),
        type: "text",
        width: 400,
        readOnly: true,
      },
    ];

    constructor(props: AccessRightsMatrixProps) {
      super(props);
      this.state = {
        data: null,
        updatedAccessRights: [], // array of data that is send to back-end to apply changes in access rights
        loading: false,
        arrayOfFolders: [], // data that is consumed to jspreadsheets
        savedFolderId: null,
        searchMode: "FOLDER",
        searchQuery: "",
        errorCells: [],
        errorCellsCurrent: -1,
        closeSideBar: false,
        loadingExcel: false,
        skipUpdate: false,
        expandLoading: false,
        headerBackgroundColors: [],
        hasRemovedUsers: false,
      };
      this.jspreadsheetRef = React.createRef();

      this.search = debounce(() => {
        this.jspreadsheetRef.current.instance.search(this.state.searchQuery);
      }, 500);
      this.checkUpdatesDebounce = debounce(this.checkUpdates, 500);
      this.debounceOnSelection = debounce(this.onSelection, 250);
    }

    onContextMenu = (instance, x, y) => {
      const items = [];
      if (typeof y === "number") {
        const { t } = this.props;

        const handleExpand = () => {
          const clickedCell = instance.getCellFromCoords(2, y);
          const currentRowLevel = this.state.arrayOfFolders[y][3];
          // should expand folder
          let rowIdx = this.state.data.folders.findIndex((item) => item.id === this.state.arrayOfFolders[y][0]) + 1;
          // if next folder is nested - then it's already fetched.
          // So we expand group instead of fetch the subfolders again
          const nextFolderIsNested =
            !!this.state.data.folders[rowIdx] && this.state.data.folders[rowIdx].level > +currentRowLevel;
          if (nextFolderIsNested) {
            clickedCell.innerHTML = ICON_DOWN;
            let rowsToAdd = 0;
            if (rowIdx) {
              while (
                this.state.data.folders[rowIdx] &&
                this.state.data.folders[rowIdx].level > (currentRowLevel as number)
              ) {
                rowIdx = rowIdx + 1;
                rowsToAdd = rowsToAdd + 1;
              }
              if (rowsToAdd) {
                const lastRowIdx = y + rowsToAdd;
                this.jspreadsheetRef.current.instance.resetRowGroup(y);
                // for nested folders - close already closed row groups
                // because they are getting opened when parent folder is expanded
                for (let index = y + 1; index <= lastRowIdx; index++) {
                  if (this.jspreadsheetRef.current.instance.rows[index]?.group) {
                    this.jspreadsheetRef.current.instance.closeRowGroup(index);
                  }
                }
              }
            }
          } else {
            this.requestExpandSubfolder(clickedCell, this.state.data.folders?.[y].id);
          }
          this.jspreadsheetRef.current.instance.resetSelection();
        };

        const handleCollapse = () => {
          this.collapseFolder(y);
        };

        const handleCollapseAllSubfolders = () => {
          this.collapseAllSubfolders(y);
        };
        const rowData = instance.getRowData(y);
        const hasNoChildren = !rowData[1];
        items.push(
          {
            title: t("Expand"),
            onclick: handleExpand,
            disabled: hasNoChildren,
          },
          {
            title: t("Collapse"),
            onclick: handleCollapse,
            disabled: hasNoChildren,
          },
          {
            title: t("Collapse all subfolders"),
            onclick: handleCollapseAllSubfolders,
            disabled: hasNoChildren,
          },
          {
            title: t("Expand all subfolders"),
            onclick: () => this.handleExpandAllSubfolders(y),
            disabled: hasNoChildren,
          }
        );
      }
      return items;
    };

    handleExpandAllSubfolders = async (y: number) => {
      const arrowCell = this.jspreadsheetRef.current.getCellFromCoords(2, y);

      const folderId = +this.state.arrayOfFolders[y][0];
      const currentRowLevel = this.state.arrayOfFolders[y][3];
      let rowIdx = y + 1;
      let rowsToRemove = 0;
      while (this.state.arrayOfFolders[rowIdx] && this.state.arrayOfFolders[rowIdx]?.[3] > currentRowLevel) {
        rowIdx = rowIdx + 1;
        rowsToRemove = rowsToRemove + 1;
      }
      if (rowsToRemove) {
        const newArrayOfFolders = this.state.arrayOfFolders.slice();
        newArrayOfFolders.splice(y + 1, rowsToRemove);
        this.jspreadsheetRef.current.instance.deleteRow(y + 1, rowsToRemove);
        await new Promise((res) => {
          this.setState({ arrayOfFolders: newArrayOfFolders }, async () => {
            res(undefined);
          });
        });
      }
      await this.requestExpandSubfolder(arrowCell, folderId, true);
      this.jspreadsheetRef.current.instance.resetSelection();
    };

    collapseAllSubfolders = (rowIndex: number) => {
      const currentRowFolderLevel = this.state.arrayOfFolders[rowIndex][3];
      const rowsToCollapse: number[] = [];
      let index = rowIndex + 1;
      // 3 - is the position of the "level" property in the data for the jspreadsheet (reduceArrayOfFolders function)
      // check wether the subfolder is a child of the clicked one, and is expanded
      while (this.state.arrayOfFolders[index]?.[3] > currentRowFolderLevel) {
        if (this.state.arrayOfFolders[index][1] === true) {
          const cellText = this.jspreadsheetRef.current.instance.getCellFromCoords(2, index).innerText.trim();
          if (cellText === ICON_DOWN) {
            rowsToCollapse.push(index);
          }
        }
        index++;
      }
      rowsToCollapse.forEach((rowIdx) => {
        this.collapseFolder(rowIdx);
      });
    };

    mapFolderIdToRowNr = (folders: API_RightsMatrixFolderData[]) => {
      this.folderIdToRow = {};
      folders.forEach((item, idx) => {
        this.folderIdToRow[item.id] = idx + 1;
      });
    };

    reduceArrayOfFolders = (folders: API_RightsMatrixFolderData[], errorCells: ErrorColumn[]) => {
      const arrayOfFolders = folders.reduce((array, current, currentIdx, source) => {
        const { accessRights, ...rest } = current;
        accessRights.forEach((item, idx) => {
          if (item.type === "CONFLICT" || item.type === "SUSPICIOUS") {
            errorCells.push({
              rowNumber: currentIdx,
              colNumber: idx,
              value: item,
            });
          }
        });
        const next = source[currentIdx + 1];
        let arrowIcon: string;
        if (rest.children) {
          if (next?.level > rest.level) {
            arrowIcon = ICON_DOWN;
          } else {
            arrowIcon = ICON_RIGHT;
          }
        } else {
          arrowIcon = "";
        }
        array.push([
          rest.id,
          rest.children,
          arrowIcon,
          rest.level,
          new Array(rest.level + 1).join("— ") + rest.name, // adding spaces to indent nested folders
          ...accessRights,
        ]);
        return array;
      }, []);
      return arrayOfFolders;
    };

    fetch = async () => {
      try {
        this.setState({ loading: true });
        const response: API_RightsMatrixResponse = await API.getData(
          API_URL.FOLDERS.getAvailableFolderRights_1(projectsDataStore.projectId, this.state.savedFolderId)
        );
        if (!response) {
          this.props.hideModal();
          notificationError(
            this.props.t("Error"),
            this.props.t(
              // eslint-disable-next-line max-len
              "The access rights matrix cannot be opened. Please try it in a subfolder or assign the rights over the action menu"
            ),
            true
          );
          throw new Error("Response is empty");
        }
        const errorCells: ErrorColumn[] = [];
        const arrayOfFolders = this.reduceArrayOfFolders(response.folders, errorCells);
        this.mapFolderIdToRowNr(response.folders);
        const hasRemovedUsers = response.userAndGroups.some((item) => item.type === "REMOVED_USER");
        const headerBackgroundColors = [].concat(
          Array(5),
          response.userAndGroups.map((item) => {
            switch (item.type) {
              case "GROUP":
                return "rgb(196, 235, 232)";
              case "REMOVED_USER":
                return "rgb(200, 67, 78)";
              default: // USER
                return "rgb(187, 191, 199)";
            }
          })
        );
        this.setState({ data: response, arrayOfFolders, errorCells, headerBackgroundColors, hasRemovedUsers }, () => {
          this.jspreadsheetRef.current?.reInitialize();
          // go to and change background of folder name for which the ARM was opened
          const focusedRowIdx = arrayOfFolders.findIndex((item) => item[0] === this.props.folderId);
          if (focusedRowIdx !== -1) {
            this.jspreadsheetRef.current?.instance.goto(focusedRowIdx);
            const cell = this.jspreadsheetRef.current?.instance.getCellFromCoords(4, focusedRowIdx);
            if (cell) {
              cell.style.backgroundColor = "#ccfbf9";
            }
          }
        });
      } catch (error) {
        throw error;
      } finally {
        this.setState({ loading: false });
      }
    };

    checkUpdates = async () => {
      const { t } = this.props;
      let destroy: MessageType;

      try {
        destroy = message.loading(t("Check in progress..."), 0);
        const formBody: API_RightMatrixUpdate = {
          updatedColumns: this.state.updatedAccessRights,
          loadedFolderIds: orderBy(Object.entries(this.folderIdToRow), (i) => i[1]).map((x) => +x[0]),
        };
        const response: API_RightsMatrixResponse = await API.newData(
          API_URL.FOLDERS.checkUpdates(projectsDataStore.projectId, this.state.savedFolderId),
          formBody
        );
        const errorCells = [].concat(this.state.errorCells);
        let newErrorCellIdx = -1;
        let areErrorCellsChanged = false;
        const dataFolders = this.state.data.folders.slice();
        const arrayOfFolders = this.state.arrayOfFolders.slice();

        response.folders.forEach((folder) => {
          // write updated access rights to "data" folders: stored back-end response
          // - it is used when extend collapsed folders
          const dataFolder = dataFolders.find((item) => item.id === folder.id);
          dataFolder.accessRights = folder.accessRights.slice();
          // write updated access data to "arrayOfFolders" - it is used when collapsing folders
          const arrayOfFoldersRecordIdx = arrayOfFolders.findIndex((item) => item[0] === folder.id);
          const arrayOfFoldersRecord = arrayOfFolders[arrayOfFoldersRecordIdx];
          if (arrayOfFoldersRecord) {
            arrayOfFoldersRecord.splice(5, folder.accessRights.length, ...folder.accessRights);
          }

          folder.accessRights.forEach((value, cellIndex) => {
            try {
              const rowNr = this.folderIdToRow[folder.id];
              let cell: HTMLElement;
              if (arrayOfFoldersRecord) {
                cell = this.jspreadsheetRef.current?.getCellFromCoords(cellIndex + 5, arrayOfFoldersRecordIdx);
              }
              const errorCellIndex = errorCells.findIndex((er) => er.rowNumber === rowNr && er.colNumber === cellIndex);

              if (errorCellIndex > -1) {
                const errorCell = errorCells[errorCellIndex];
                if (value.type !== errorCell.value.type) {
                  if (value.type !== "CONFLICT" && value.type !== "SUSPICIOUS") {
                    // before we had an error, now everything is ok
                    errorCells.splice(errorCellIndex, 1);
                    if (this.state.errorCellsCurrent >= errorCells.length) {
                      newErrorCellIdx = errorCells.length - 1;
                    }
                  }
                  areErrorCellsChanged = true;
                  errorCell.type = value.type;
                }
              } else {
                if (value.type === "CONFLICT" || value.type === "SUSPICIOUS") {
                  // before we hadn't an error, now we had one
                  errorCells.push({
                    rowNumber: rowNr,
                    colNumber: cellIndex,
                    value: value,
                  });
                  areErrorCellsChanged = true;
                }
              }
              if (cell) {
                accessRightSetCellColor(value, cell);
              }
            } catch (e) {
              console.log(e);
            }
          });
        });

        if (areErrorCellsChanged) {
          errorCells.sort((a, b) =>
            a.rowNumber > b.rowNumber
              ? 1
              : a.rowNumber < b.rowNumber
              ? -1
              : a.colNumber > b.colNumber
              ? 1
              : a.colNumber < b.colNumber
              ? -1
              : 0
          );
          if (newErrorCellIdx > 0) {
            this.setState({ errorCells, errorCellsCurrent: newErrorCellIdx });
          } else {
            this.setState({ errorCells });
          }
        }
        const hasRemovedUsers = response.userAndGroups.some((item) => item.type === "REMOVED_USER");

        this.setState({
          data: { ...this.state.data, folders: dataFolders },
          arrayOfFolders,
          hasRemovedUsers,
        });
      } catch (error) {
        throw error;
      } finally {
        destroy();
      }
    };

    applyUpdates = async () => {
      const { t } = this.props;
      let destroy: MessageType;
      try {
        destroy = message.loading(t("Saving..."), 0);
        this.setState({ loading: true });
        const formBody: API_RightMatrixUpdate = {
          updatedColumns: this.state.updatedAccessRights,
          loadedFolderIds: orderBy(Object.entries(this.folderIdToRow), (i) => i[1]).map((x) => +x[0]),
        };
        const response: API_RightsMatrixResponse = await API.newData(
          API_URL.FOLDERS.applyChanges(projectsDataStore.projectId, this.state.savedFolderId),
          formBody
        );
        const errorCells = [];
        const arrayOfFolders = this.reduceArrayOfFolders(response.folders, errorCells);
        // this.mapFolderIdToRowNr(arrayOfFolders);

        // when user/group was deleted or extended with some rights - we have to be sure it affected on columns of table
        const wasColumnsChanged = this.state.data.userAndGroups.length !== response.userAndGroups.length;
        const hasRemovedUsers = response.userAndGroups.some((item) => item.type === "REMOVED_USER");
        this.setState(
          {
            data: response,
            hasRemovedUsers,
            arrayOfFolders,
            updatedAccessRights: [],
            errorCells,
            skipUpdate: true,
          },
          () => {
            if (wasColumnsChanged) {
              // the only way I found to reset correct columns - is to reinitialize table...
              this.jspreadsheetRef.current?.reInitialize();
            } else {
              // because just passing data to jspreadsheets through props is not enough to update cells
              // styles - set data to by using jspreadsheets method.
              this.jspreadsheetRef.current?.instance?.setData(this.state.arrayOfFolders.slice());
            }
          }
        );
      } catch (error) {
        throw error;
      } finally {
        this.setState({ loading: false });
        destroy();
      }
    };

    componentWillUnmount() {
      window.removeEventListener("beforeunload", this.handleLeavePage, true);
    }

    componentDidMount() {
      window.addEventListener("beforeunload", this.handleLeavePage, true);
      this.setState({ savedFolderId: this.props.folderId }, this.fetch);
    }

    handleLeavePage = (e) => {
      e = e || window.event;
      if (this?.state?.updatedAccessRights?.length > 0) {
        const confirmationMessage = "Sure?";
        e.returnValue = confirmationMessage;
        return confirmationMessage;
      }
      return "";
    };

    collapseFolder = (rowIndex: number) => {
      // 3 - is the position of the "level" property in the data for the jspreadsheet (reduceArrayOfFolders function)
      const currentRowLevel = this.state.arrayOfFolders[rowIndex][3];
      let rowIdx = rowIndex + 1;
      let rowsToRemove = 0;
      while (this.state.arrayOfFolders[rowIdx] && this.state.arrayOfFolders[rowIdx]?.[3] > currentRowLevel) {
        rowIdx = rowIdx + 1;
        rowsToRemove = rowsToRemove + 1;
      }
      // 2 - is the position of chevron icon that indicates the collapsed/expanded folder
      const clickedCell = this.jspreadsheetRef.current?.instance?.getCellFromCoords(2, rowIndex);
      clickedCell.innerHTML = ICON_RIGHT;
      if (rowsToRemove) {
        const errorCells = this.state.errorCells.filter(
          (item) => !(item.rowNumber > rowIndex && item.rowNumber < rowIndex + rowsToRemove)
        );
        this.setState({
          errorCells,
        });

        this.jspreadsheetRef.current.instance.setRowGroup(+clickedCell.dataset.y, rowsToRemove);
        // this.jspreadsheetRef.current.instance.deleteRow(y1 + 1, rowsToRemove);
      }
    };

    onSelection: Jspreadsheet["onSelection"] = async (_, x1, y1) => {
      console.log("onSelection", _, x1, y1);

      // clicked on expand/collapse cell
      if (x1 === 2) {
        const clickedCell = this.jspreadsheetRef.current?.instance?.getCellFromCoords(x1, y1);
        // in Safari the innerText returns a string with linebreak
        if (clickedCell) {
          const cellText = clickedCell.innerText.trim();
          const currentRowLevel = this.state.arrayOfFolders[y1][3];
          if (cellText === ICON_DOWN) {
            // should collapse folder
            this.collapseFolder(y1);
          } else if (cellText === ICON_RIGHT) {
            // should expand folder
            let rowIdx = this.state.data.folders.findIndex((item) => item.id === this.state.arrayOfFolders[y1][0]) + 1;
            // if next folder is nested - then it's already fetched.
            // So we expand group instead of fetch the subfolders again
            const nextFolderIsNested =
              !!this.state.data.folders[rowIdx] && this.state.data.folders[rowIdx].level > +currentRowLevel;
            if (nextFolderIsNested) {
              let rowsToAdd = 0;
              if (rowIdx) {
                while (
                  this.state.data.folders[rowIdx] &&
                  this.state.data.folders[rowIdx].level > (currentRowLevel as number)
                ) {
                  rowIdx = rowIdx + 1;
                  rowsToAdd = rowsToAdd + 1;
                }
                if (rowsToAdd) {
                  clickedCell.innerHTML = ICON_DOWN;
                  const lastRowIdx = y1 + rowsToAdd;
                  this.jspreadsheetRef.current.instance.resetRowGroup(y1);
                  // for nested folders - close already closed row groups
                  // because they are getting opened when parent folder is expanded
                  for (let index = y1 + 1; index <= lastRowIdx; index++) {
                    if (this.jspreadsheetRef.current.instance.rows[index]?.group) {
                      this.jspreadsheetRef.current.instance.closeRowGroup(index);
                    }
                  }
                }
              }
            } else {
              // Fetch subfolder
              const folderId = this.state.arrayOfFolders[y1][0];
              // get row number from cell because expanding folders simultaneously would
              // make "y1" from the execution context invalid

              await this.requestExpandSubfolder(clickedCell, +folderId);
            }

            this.jspreadsheetRef.current.instance.resetSelection();
          }
        }
      }
    };

    requestExpandSubfolder = async (cell: HTMLElement, folderId: number, expandAll?: boolean) => {
      try {
        cell.innerHTML = `<div class="rotate">${ICON_SPIN}</div>`;
        await until(() => !this.state.expandLoading, 100);
        this.setState({ expandLoading: true });
        const formBody: API_RightMatrixUpdate = {
          updatedColumns: this.state.updatedAccessRights,
        };
        let response;
        if (expandAll) {
          response = await API.newData<API_RightsMatrixResponse>(
            API_URL.FOLDERS.expandAllSubfoldersForMatrixFolder(projectsDataStore.projectId, folderId),
            formBody
          );
        } else {
          response = await API.newData<API_RightsMatrixResponse>(
            API_URL.FOLDERS.getSubfoldersForMatrixFolder(projectsDataStore.projectId, folderId),
            formBody
          );
        }
        cell.innerHTML = ICON_DOWN;

        const foldersToAdd = response.folders;
        const rowNr = +cell.dataset.y;
        const errorCells: ErrorColumn[] = [];
        const newArrayOfFolders = this.state.arrayOfFolders.slice();
        const newDataFolders = this.state.data.folders.slice();

        const arrayOfFolders = this.reduceArrayOfFolders(foldersToAdd, errorCells);
        errorCells.forEach((cell) => (cell.rowNumber += rowNr + 1));
        const newErrorCells = this.state.errorCells.concat(errorCells).sort((a, b) => a.rowNumber - b.rowNumber);

        const position = newDataFolders.findIndex((item) => item.id === folderId);
        // if the folder was already expanded - clean up the previously expanded folders and set the new ones
        let amountOfFoldersToRemove: number = null;
        if (newDataFolders[position + 1]?.level > newDataFolders[position]?.level) {
          let i = position + 1;
          while (newDataFolders[i]?.level > newDataFolders[position]?.level) {
            i++;
            amountOfFoldersToRemove += 1;
          }
        }
        newDataFolders.splice(position + 1, amountOfFoldersToRemove, ...foldersToAdd);
        // console.log("newArrayOfFolders before splice", newArrayOfFolders.length);
        // console.log("rowNr", rowNr);
        newArrayOfFolders.splice(rowNr + 1, null, ...arrayOfFolders);
        // console.log("position", position);
        // console.log("amountOfFoldersToRemove", amountOfFoldersToRemove);
        // console.log("arrayOfFolders", arrayOfFolders);
        // console.log("newDataFolders", newDataFolders);
        // console.log("newArrayOfFolders", newArrayOfFolders);
        this.mapFolderIdToRowNr(newDataFolders);

        this.setState({
          arrayOfFolders: newArrayOfFolders,
          data: { userAndGroups: this.state.data.userAndGroups, folders: newDataFolders },
          errorCells: newErrorCells,
        });
        this.jspreadsheetRef.current?.instance.insertRow(arrayOfFolders.length, rowNr, false, arrayOfFolders);
        this.jspreadsheetRef.current.instance.resetRowGroup(rowNr);
      } catch (error) {
        cell.innerHTML = ICON_RIGHT;
        throw error;
      } finally {
        this.jspreadsheetRef.current.instance.resetSelection();
        this.setState({ expandLoading: false });
      }
    };

    updateTable = (
      instance: jspreadsheet.worksheetInstance,
      cellUpdates: JspreadsheetCellUpdate<API_ColumnValueUpdate>[]
    ) => {
      if (this.state.skipUpdate) {
        this.setState({ skipUpdate: false });
        return;
      }
      if (cellUpdates) {
        const newData = this.state.arrayOfFolders.slice();
        const uniqueUpdatedAccessRights = this.state.updatedAccessRights.slice();
        const updatedValues = [];
        cellUpdates.forEach((cellUpdate) => {
          const row = this.jspreadsheetRef.current.instance.getRow(+cellUpdate.y);
          // restore setting updates for hidden folders
          // (because ignoring turns out less optimized technically)
          if (row.visible === false) {
            this.jspreadsheetRef.current.instance.updateCell(
              +cellUpdate.x,
              +cellUpdate.y,
              cellUpdate.oldValue?.accessRight || (cellUpdate.oldValue as string)
            );
            return;
          }
          if (cellUpdate.value === undefined) {
            // should never happen but better have a warning.
            // solved with ===undefined and changed the previous !cellData.value because,
            // !"" is also true and setting an empty string must be allowed to reset a right in a cell
            console.warn("cellData.value is undefined!");
            return;
          }

          if (cellUpdate.oldValue === undefined) {
            // should now also never happen again but in PAVE-2104 it was the case and results in a error 500
            // so anyway, I add a console warning
            console.warn("cellData.oldValue is undefined!");
          }
          // because after changing cell via select box value returns just a string of accessRight we need to
          // re-create lost object and provide folderId because it's needed for back-end to process updates
          const cellUpdateData = newData[+cellUpdate.y];
          const valueFromBackend = {
            ...this.state.data.folders[+cellUpdate.y].accessRights[+cellUpdate.x - this.basicColumns.length],
          };
          const updatedValue = {
            ...valueFromBackend,
            accessRight:
              typeof cellUpdate.value === "string"
                ? cellUpdate.value
                : (cellUpdate.value as API_ColumnValue)?.accessRight,
            folderId: cellUpdateData[0],
          } as API_ColumnValueUpdate;
          // and assign it to array of data
          newData[+cellUpdate.y][+cellUpdate.x] = updatedValue;
          if (cellUpdate.oldValue?.accessRight !== updatedValue.accessRight) {
            instance.setValueFromCoords(+cellUpdate.x, +cellUpdate.y, updatedValue.accessRight);
          }
          // when multiple changes happen for single rule - overwrite it with latest change
          const foundIdx = uniqueUpdatedAccessRights.findIndex((item) => {
            return (
              updatedValue.id === item.id &&
              updatedValue.objectType === item.objectType &&
              item.folderId === updatedValue.folderId
            );
          });
          if (foundIdx !== -1) {
            uniqueUpdatedAccessRights.splice(foundIdx, 1);
          }
          updatedValues.push(updatedValue);
        });

        this.setState({
          arrayOfFolders: newData,
          updatedAccessRights: uniqueUpdatedAccessRights.concat(updatedValues),
          // set status to unchecked because changes have to be checked by back-end
        });
        this.checkUpdatesDebounce();
      }
    };

    onBeforeSearch = () => {
      const { data, searchMode, searchQuery } = this.state;
      if (!searchQuery) {
        return undefined;
      }
      if (searchMode !== "FOLDER") {
        const query = searchQuery.toLowerCase();
        const usersOrGroupsMap = data.userAndGroups.reduce((obj, item) => {
          if (item.type === searchMode && item.name.toLowerCase().indexOf(query) !== -1) {
            obj[item.id] = true;
          }
          return obj;
        }, {});
        const visibleRowIndexes = data.folders.reduce((array, item, idx) => {
          if (
            item.accessRights.some(
              (right) => right.type !== "NO_ACCESS" && right.objectType === searchMode && usersOrGroupsMap[right.id]
            )
          ) {
            array.push(idx);
          }
          return array;
        }, []);
        // Set the rowIds that should be return to the user.
        // For this example the return will be [5,21]
        return visibleRowIndexes as [];
      }
      // let default jspreadsheets function search by folder name
      return undefined;
    };

    handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({ searchQuery: e.target.value }, this.search);
    };

    handleSelectChange = (searchMode: SearchMode) => {
      this.setState({ searchMode }, this.search);
    };

    resetSearch = () => {
      this.setState({ searchQuery: "", searchMode: "FOLDER" }, this.search);
    };

    get saveDisabled() {
      return this.state.updatedAccessRights.length === 0;
    }

    download = async () => {
      try {
        this.setState({ loadingExcel: true });
        const file: File = await API.getBlob(API_URL.REPORTS.getAccessRightsAsFile(projectsDataStore.projectId));
        download(file, file.name, "text/vnd.ms-excel");
      } catch (error) {
        throw error;
      } finally {
        this.setState({ loadingExcel: false });
      }
    };

    getErrorIdxAfterSelectionRange(): number {
      const selectedCells = this.jspreadsheetRef?.current.getSelectedCells();
      if (selectedCells.length === 0) {
        return 0;
      }
      const row: number = +selectedCells[selectedCells.length - 1].dataset.y;
      const col: number = +selectedCells[selectedCells.length - 1].dataset.x;
      return this.state.errorCells.findIndex((error) => {
        return (row === error.rowNumber && error.colNumber + 5 > col) || row < error.rowNumber;
      });
    }

    getErrorIdxBeforeSelectionRange(): number {
      const selectedCells = this.jspreadsheetRef?.current.getSelectedCells();
      if (selectedCells.length === 0) {
        return this.state.errorCells.length - 1;
      }
      const row: number = +selectedCells[0].dataset.y;
      const col: number = +selectedCells[0].dataset.x;
      const errorsBefore = [];
      this.state.errorCells.forEach((error, idx) => {
        if ((row === error.rowNumber && error.colNumber + 5 < col) || row > error.rowNumber) {
          errorsBefore.push(idx);
        }
      });
      return errorsBefore.length > 0 ? errorsBefore[errorsBefore.length - 1] : errorsBefore.length - 1;
    }

    handleSetError(errorIdx: number) {
      const error = this.state.errorCells[errorIdx];
      this.jspreadsheetRef.current.instance.goto(error.rowNumber, error.colNumber + 5);

      this.jspreadsheetRef?.current?.updateSelectionFromCoords(
        error.colNumber + 5,
        error.rowNumber,
        error.colNumber + 5,
        error.rowNumber
      );
    }

    handleLastError = () => {
      let newCurrent = this.getErrorIdxBeforeSelectionRange();
      if (newCurrent === -1) {
        newCurrent = this.state.errorCells.length - 1;
      }
      this.handleSetError(newCurrent);
      this.setState({ errorCellsCurrent: newCurrent });
    };

    handleNextError = () => {
      let newCurrent = this.getErrorIdxAfterSelectionRange();
      if (newCurrent === -1) {
        newCurrent = 0;
      }

      this.handleSetError(newCurrent);
      this.setState({ errorCellsCurrent: newCurrent });
    };

    handleErrorClick = (rowNumber: number) => {
      const firstErrorInRow = this.state.errorCells.findIndex((error) => error.rowNumber === rowNumber);
      if (firstErrorInRow > -1) {
        this.handleSetError(firstErrorInRow);
      }
    };

    renderErrorSidebarItem(rowNumber, errors) {
      const { t } = this.props;

      return (
        <div onClick={() => this.handleErrorClick(rowNumber)} style={{ cursor: "pointer" }}>
          {t("Row {{row}}: {{errors}} errors and {{warnings}} warnings", {
            row: rowNumber + 1,
            errors: errors.filter((err) => err.value.type === "CONFLICT").length,
            warnings: errors.filter((err) => err.value.type === "SUSPICIOUS").length,
          })}
        </div>
      );
    }

    renderErrorSidebar() {
      const { t } = this.props;
      const groupedByRow = groupBy(this.state.errorCells, "rowNumber");
      return (
        <div style={{ padding: 5, whiteSpace: "nowrap" }}>
          <h3>{t("Error list")}</h3>
          <div style={{ marginBottom: 10, textAlign: "center" }}>
            {t("{{errors}}  errors and warnings in {{rows}} rows", {
              errors: this.state.errorCells.length,
              rows: Object.keys(groupedByRow).length,
            })}
          </div>
          {Object.keys(groupedByRow).map((rowNumber) => {
            return this.renderErrorSidebarItem(parseInt(rowNumber), groupedByRow[rowNumber]);
          })}
        </div>
      );
    }

    get columns(): jspreadsheet.Column[] {
      return this.basicColumns.concat(
        this.state.data.userAndGroups.map((item) => ({
          title: item.name,
          type: AccessRightsCell,
          width: 25,
          rotate: 90,
        }))
      );
    }

    onHoverHeader = (e: MouseEvent) => {
      if (!e.target || !this.state.data?.userAndGroups) {
        return;
      }
      const target = e.target as HTMLElement;
      const cell = target.nodeName === "TD" ? target : target.closest("td");
      if (!cell) {
        return;
      }
      const columnIndex = +cell.dataset.x;
      if (Number.isNaN(columnIndex)) {
        return;
      }
      const item = this.state.data.userAndGroups[columnIndex - this.basicColumns.length];
      if (!item) {
        return;
      }
      if (item.type === "GROUP") {
        simplePopover(cell, item.name || "");
        return;
      }
      if (item.type === "USER" || item.type === "REMOVED_USER") {
        simplePopover(
          cell,
          crE("div")
            .ac(crE("div", String(item.id ?? "")))
            .ac(crE("div", item.email || ""))
        );
      }
    };

    render() {
      const { t } = this.props;
      const { data, arrayOfFolders, loading, errorCells: _, loadingExcel, updatedAccessRights } = this.state;

      if (!data) {
        if (loading) {
          return <CanvasLoader loadingText={t("Loading matrix...")} />;
        }
        return null;
      }

      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ paddingRight: "40px" }}>
            <div className="viewer-modal-title">{t("Access right matrix")}</div>
            <Row style={{ minHeight: 45 }} gutter={10} justify="space-between">
              <Col className="flex-center" style={{ marginBottom: 5 }}>
                {/* {errorCells.length > 0 ? (
                  <>
                    <Button
                      style={{ marginRight: 10 }}
                      disabled={this.state.searchQuery !== ""}
                      faIcon={"chevron-left"}
                      onClick={this.handleLastError}
                      grey={true}
                    >
                      {t("Former error")}
                    </Button>
                    <Button
                      style={{ marginRight: 10 }}
                      disabled={this.state.searchQuery !== ""}
                      faIcon={"chevron-right"}
                      onClick={this.handleNextError}
                      grey={true}
                    >
                      {t("Next error")}
                    </Button>
                  </>
                ) : (
                  ""
                )} */}
                <div style={{ display: "inline-block" }}>
                  <Input
                    prefix={<FontAwesomeIcon icon={["fas", "search"]} size="1x" fixedWidth={true} />}
                    value={this.state.searchQuery}
                    onChange={this.handleInputChange}
                    addonAfter={
                      <Select onChange={this.handleSelectChange} value={this.state.searchMode} style={{ width: 150 }}>
                        <Select.Option key="GROUP" value="GROUP">
                          {t("Group")}
                        </Select.Option>
                        <Select.Option key="USER" value="USER">
                          {t("User")}
                        </Select.Option>
                        <Select.Option key="FOLDER" value="FOLDER">
                          {t("Directory")}
                        </Select.Option>
                      </Select>
                    }
                    suffix={
                      this.state.searchQuery && (
                        <span className="pointer color-grey" onClick={this.resetSearch}>
                          <FontAwesomeIcon icon={["far", "times"]} fixedWidth={true} size="lg" />
                        </span>
                      )
                    }
                  />
                </div>
              </Col>
              <Col style={{ marginBottom: 5 }}>
                <Space>
                  {this.state.hasRemovedUsers ? <RemovedUsersNote /> : null}
                  <AccessRightsExplanation />
                  <Popconfirm title={t("This will export the last saved version.")} onConfirm={() => this.download()}>
                    <Button loading={loadingExcel} faIcon={["far", "file-excel"]}>
                      {t("Export")}
                    </Button>
                  </Popconfirm>

                  <Button type="primary" onClick={this.applyUpdates} disabled={this.saveDisabled} loading={loading}>
                    {t("Save")}
                  </Button>
                  {updatedAccessRights.length > 0 ? (
                    <Popconfirm
                      title={
                        <>
                          <div>{t("There are unsaved changes.")}</div>
                          <div>{t("Do you want to save them?")}</div>
                        </>
                      }
                      onConfirm={() => {
                        this.applyUpdates();
                        this.props.hideModal();
                      }}
                      onCancel={this.props.hideModal}
                      okText={t("Yes")}
                      cancelText={t("No, discard changes and close the window")}
                    >
                      <ModalCloseBtn className="relative-position" />
                    </Popconfirm>
                  ) : (
                    <ModalCloseBtn onClick={this.props.hideModal} className="relative-position" />
                  )}
                </Space>
              </Col>
            </Row>
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            {/* <GenericSidebar
              context={this}
              shouldClose={this.state.closeSideBar}
              renderContent={this.renderErrorSidebar}
            >
                */}
            <Jspreadsheet
              ref={this.jspreadsheetRef}
              headerBackgroundColors={this.state.headerBackgroundColors}
              worksheets={[
                {
                  data: arrayOfFolders,
                  columns: this.columns,
                  tableOverflow: true,
                  freezeColumns: 5,
                  allowManualInsertRow: false,
                  allowInsertColumn: false,
                },
              ]}
              loadingSpin={loading}
              stretchHeight={true}
              headerWith90Degree={[4]}
              className="access-rights"
              onselection={this.debounceOnSelection}
              onafterchanges={this.updateTable}
              onbeforesearch={this.onBeforeSearch}
              contextMenu={this.onContextMenu}
              onHoverHeader={this.onHoverHeader}
            />
            {/* </GenericSidebar> */}
          </div>
        </div>
      );
    }
  }
);
